import { MapPin, Phone, Clock, ExternalLink, MessageCircle, Globe } from "lucide-react";
import { SectionTitle } from "../components";
import { track } from "../api";
import { useSeo } from "../seo";

const PHONES = {
  restaurant: { display: "(312) 955-0331", tel: "+13129550331" },
  catering: { display: "312-955-0463", tel: "+13129550463" },
};

const LOCATIONS = [
  {
    name: "Lincoln Park",
    brand: "Urban by VeerGi — Chicago",
    address: "2660 N. Halsted St, Chicago, IL 60614",
    phone: "312-955-0331",
    tel: "+13129550331",
    hours: [["Daily", "2:00 PM – 11:00 PM"]],
    mapQuery: "Urban Turban by VeerGi, 2660 N Halsted St, Chicago, IL 60614",
  },
];

const FAQS = [
  { q: "Where is Urban by VeerGi located?",
    a: "Lincoln Park at 2660 N. Halsted St, Chicago, IL 60614 — (312) 955-0331 (restaurant), 312-955-0463 (catering). A second location in Hoffman Estates opens October 2026." },
  { q: "What are the hours?",
    a: "Lincoln Park is open daily 2:00 PM – 11:00 PM. Hoffman Estates opens October 2026." },
  { q: "What kind of food does Urban by VeerGi serve?",
    a: "Punjabi cuisine — curries, biryani and tandoor breads. The Urban family: Urban Turban and Urban Express are open now." },
  { q: "Can I dine in, or order pickup and delivery?",
    a: "Yes. Dine in at Lincoln Park, or browse the menu on MyUrbanPassport and continue to pickup or delivery checkout online. You can also call (312) 955-0331 (restaurant) or 312-955-0463 (catering)." },
  { q: "What is MyUrbanPassport?",
    a: "A free digital passport for the Urban family — explore the menu, hear the story behind each dish, earn Urban Points on qualifying orders and play the Urban Arcade." },
];

const LocationCard = ({ loc }) => (
  <div data-testid={`location-${loc.name.toLowerCase().replace(/ /g, "-")}`} className="rounded-2xl bg-[#121212] border border-white/10 overflow-hidden">
    <iframe
      title={`Map — ${loc.name}`}
      src={`https://www.google.com/maps?q=${encodeURIComponent(loc.mapQuery)}&output=embed`}
      className="w-full h-44 border-0 grayscale-[35%] contrast-[1.05]"
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
    <div className="p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#D4AF37]">{loc.name.toUpperCase()}</p>
      <h3 className="font-display text-2xl font-semibold mt-1">{loc.brand}</h3>
      <div className="flex items-start gap-2.5 mt-4 text-sm text-white/75">
        <MapPin size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
        <span>{loc.address}</span>
      </div>
      <a
        href={`tel:${loc.tel}`}
        data-testid={`location-phone-${loc.name.toLowerCase().replace(/ /g, "-")}`}
        onClick={() => track("location_call_tapped", { location: loc.name })}
        className="flex items-center gap-2.5 mt-3 text-sm text-[#D4AF37] font-bold"
      >
        <Phone size={15} className="shrink-0" />
        {loc.phone}
      </a>
      <a
        href={`tel:${PHONES.catering.tel}`}
        data-testid={`location-catering-${loc.name.toLowerCase().replace(/ /g, "-")}`}
        onClick={() => track("location_catering_tapped", { location: loc.name })}
        className="flex items-center gap-2.5 mt-3 text-sm text-[#D4AF37] font-bold"
      >
        <MessageCircle size={15} className="shrink-0" />
        Catering {PHONES.catering.display}
      </a>
      <a
        href="https://tryurbanturban.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`location-website-${loc.name.toLowerCase().replace(/ /g, "-")}`}
        onClick={() => track("location_website_tapped", { location: loc.name })}
        className="flex items-center gap-2.5 mt-3 text-sm text-white/75"
      >
        <Globe size={15} className="text-[#D4AF37] shrink-0" />
        TryUrbanTurban.com
      </a>
      <div className="flex items-start gap-2.5 mt-3 text-sm text-white/75">
        <Clock size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
        <div>
          {loc.comingSoon
            ? <p data-testid={`location-coming-soon-${loc.name.toLowerCase().replace(/ /g, "-")}`} className="text-[#D4AF37] font-bold">Coming Soon</p>
            : loc.hours.map(([d, h]) => (
                <p key={d}><span className="text-white/45">{d}:</span> {h}</p>
              ))}
        </div>
      </div>
      <button
        data-testid={`location-directions-${loc.name.toLowerCase().replace(/ /g, "-")}`}
        onClick={() => {
          track("directions_tapped", { location: loc.name });
          window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.mapQuery)}`, "_blank", "noopener");
        }}
        className="mt-5 w-full flex items-center justify-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] text-xs font-bold rounded-full py-3 tracking-widest hover:bg-[#D4AF37]/10"
      >
        <ExternalLink size={13} />
        GET DIRECTIONS
      </button>
    </div>
  </div>
);

export default function LocationsPage() {
  useSeo({
    title: "Locations & Hours",
    description: "Urban by VeerGi locations: Lincoln Park — 2660 N. Halsted St, Chicago (daily 2–11 PM). Hoffman Estates — opening October 2026.",
    path: "/experience/locations",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });
  return (
    <div data-testid="locations-page" className="px-4 pt-6 pb-10 mx-auto w-full max-w-3xl">
      <SectionTitle kicker="Visit us" title="Find your Urban" testId="locations-title" />
      <div className="space-y-5">
        {LOCATIONS.map((loc) => <LocationCard key={loc.name} loc={loc} />)}
        <div data-testid="location-hoffman-teaser" className="rounded-2xl bg-[#121212] border border-white/10 p-5 flex items-center gap-3">
          <MapPin size={16} className="text-[#D4AF37] shrink-0" />
          <p className="text-sm text-white/75"><span className="font-bold text-white">Hoffman Estates</span> — opening October 2026</p>
        </div>
      </div>
      <div className="mt-10">
        <SectionTitle kicker="Good to know" title="Questions, answered" testId="locations-faq-title" />
        <div className="space-y-3" data-testid="locations-faq">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`faq-item-${i}`} className="rounded-2xl bg-[#121212] border border-white/10 p-5">
              <p className="text-sm font-bold text-[#D4AF37]">{f.q}</p>
              <p className="text-sm text-white/70 leading-relaxed mt-2">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
